import { CallToActionButton, FlatButton } from '@finn-no/troika-react-buttons';
import { Modal, ModalContent, ModalFooter } from '@finn-no/troika-react-modal';
import React, { useState } from 'react';
import DOMPurify from 'dompurify';

import FeedbackRating from './FeedbackRating';
import FeedbackText from './FeedbackText';

type FeedbackModalProps = {
    isOpen: boolean;
    closeModal: () => void;
    completed: () => void;
    url: string;
    type: import('../types').FeedbackType;
    addOnInfo?: string;
};

const FeedbackModal: React.FC<FeedbackModalProps> = (props) => {
    const { isOpen, closeModal, completed, url, type, addOnInfo } = props;
    const LOCAL_STORAGE_FEEDBACK_STR = 'finn_notifications_feedback_is_set';

    const [firstPage, setFirstPage] = useState(true);
    const [chosenFace, setChosenFace] = useState();
    const [textfieldFeedback, setTextfieldFeedback] = useState('');
    const [loading, setLoading] = useState(false);

    const setDisabledRating = (input) => {
        if (chosenFace !== undefined && input !== chosenFace) {
            return true;
        }
        return false;
    };

    const clearFeedback = () => {
        setChosenFace(undefined);
        setTextfieldFeedback('');
        setFirstPage(true);
    };

    const setRating = (value) => {
        if (chosenFace === undefined) {
            setChosenFace(value);
        } else setChosenFace(undefined);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        setLoading(true);

        const feedbackText =
            textfieldFeedback === '' ? null : textfieldFeedback;

        const bodyContent = {
            userRating: chosenFace,
            userRatingText: DOMPurify.sanitize(feedbackText),
            userKeyword: DOMPurify.sanitize(addOnInfo),
        };

        const success = () => {
            setLoading(false);
            clearFeedback();
            closeModal();
            completed();
        };

        const error = (response) => {
            setLoading(false);
            closeModal();
            // console.error('REST error', response);
        };

        const fetchURL = feedbackText
            ? `${url}/api/feedback/${type}`
            : `${url}/api/feedback/`;

        fetch(fetchURL, {
            method: 'POST',
            body: JSON.stringify(bodyContent),
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((res) => {
                if (res.ok) success();
                localStorage.setItem(LOCAL_STORAGE_FEEDBACK_STR, 'isSet');
            })
            .catch((err) => error(err));
    };

    return (
        <Modal
            aria-label="Tilbakemelding"
            isOpen={isOpen}
            onDismiss={() => closeModal()}
            max640
            roomy
        >
            <div className="w-auto">
                <FlatButton
                    className="button--has-icon float-right m-8"
                    onClick={() => closeModal()}
                >
                    <div className="icon icon--cross">
                        <span className="button__text sr-only">Lukk modal</span>
                    </div>
                </FlatButton>
            </div>
            <ModalContent>
                {firstPage ? (
                    <FeedbackRating
                        setChosenFace={(value) => setRating(value)}
                        setDisabled={(input) => setDisabledRating(input)}
                    />
                ) : (
                    <FeedbackText
                        textfieldFeedback={textfieldFeedback}
                        setTextfieldFeedback={(input) =>
                            setTextfieldFeedback(input)
                        }
                        type={type}
                    />
                )}
            </ModalContent>
            <ModalFooter>
                {firstPage ? (
                    <CallToActionButton
                        onClick={() => setFirstPage(false)}
                        disabled={chosenFace === undefined}
                    >
                        Neste
                    </CallToActionButton>
                ) : (
                    <>
                        <FlatButton onClick={() => setFirstPage(true)}>
                            Tilbake
                        </FlatButton>
                        <CallToActionButton
                            onClick={(e) => handleSubmit(e)}
                            inProgress={loading}
                        >
                            Send tilbakemelding
                        </CallToActionButton>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default FeedbackModal;
